import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { Download, Trash2, Upload } from 'lucide-react';

import type { ClientDocResponse } from '@/domain/models/client.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { DOC_CATEGORIES } from '@/shared/constants/document-categories.js';
import { formatDate } from '@/shared/utils/format-date.js';
import { formatFileSize } from '@/shared/utils/format-file-size.js';

interface Props {
    documents: ClientDocResponse[];
    uploadingCategory: string | null;
    onUploadRequest: (category: string) => void;
    onDownload: (doc: ClientDocResponse) => void;
    onDeleteRequest: (docId: string) => void;
}

const isTempId = (id: string) => id.startsWith('temp-');

const headerCellSx = { fontSize: 11, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' } as const;

export function ClientDocumentsTable({ documents, uploadingCategory, onUploadRequest, onDownload, onDeleteRequest }: Props) {
    const { translate } = useApp();

    return (
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider', boxShadow: 1, overflow: 'hidden' }}>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <TableCell sx={headerCellSx}>{translate('clients.docCategory')}</TableCell>
                            <TableCell sx={headerCellSx}>{translate('clients.docFile')}</TableCell>
                            <TableCell sx={headerCellSx}>{translate('clients.docSize')}</TableCell>
                            <TableCell sx={headerCellSx}>{translate('clients.docUploadedAt')}</TableCell>
                            <TableCell sx={headerCellSx}>{translate('clients.docUploadedBy')}</TableCell>
                            <TableCell sx={{ width: 110 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {DOC_CATEGORIES.map((cat) => {
                            const doc = documents.find((d) => d.category === cat.key);
                            const staged = doc ? isTempId(doc.id) : false;
                            return (
                                <TableRow key={cat.key} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={500}>
                                            {cat.label}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {doc ? (
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Chip label={doc.fileName} size="small" sx={{ fontSize: 11 }} />
                                                {staged && <Chip label={translate('clients.docPending')} size="small" color="warning" variant="outlined" sx={{ fontSize: 10 }} />}
                                            </Stack>
                                        ) : (
                                            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                                                {translate('clients.docNotUploaded')}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" color="text.secondary">
                                            {doc?.fileSizeBytes ? formatFileSize(Number(doc.fileSizeBytes)) : '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" color="text.disabled">
                                            {doc && !staged ? formatDate(doc.uploadedAt) : '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" color="text.disabled">
                                            {doc?.uploadedByName ?? '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5}>
                                            <IconButton
                                                size="small"
                                                aria-label={translate('clients.docUploadAria', { category: cat.label })}
                                                disabled={uploadingCategory === cat.key}
                                                onClick={() => onUploadRequest(cat.key)}
                                            >
                                                <Upload size={14} />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                aria-label={translate('clients.docDownloadAria', { category: cat.label })}
                                                disabled={!doc || staged}
                                                onClick={() => doc && onDownload(doc)}
                                            >
                                                <Download size={14} />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                aria-label={translate('clients.docDeleteAria', { category: cat.label })}
                                                color="error"
                                                disabled={!doc}
                                                onClick={() => doc && onDeleteRequest(doc.id)}
                                            >
                                                <Trash2 size={14} />
                                            </IconButton>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Card>
    );
}
